/**
 * TurnedComics Blog - Interactive Widgets
 * Version: 1.0.0 | Date: 2026-03-06
 * 
 * Features:
 * - Fan Counter Animation
 * - Testimonials Slider
 * - Poll Widget (Favorite Universe)
 * - Quiz Widget (Superhero Identity)
 * - Newsletter Signup
 * - Engagement Tracking
 * - Lazy Loading
 */

(function() {
  'use strict';

  // ==========================================================================
  // Fan Counter Animation
  // ==========================================================================
  function initFanCounter() {
    const counterElement = document.getElementById('tc-fanCounter');
    if (!counterElement) return;

    const target = 10247;
    const duration = 2500;
    const start = 0;
    const increment = (target - start) / (duration / 16);
    let current = start;

    function updateCounter() {
      current += increment;
      if (current < target) {
        counterElement.textContent = Math.floor(current).toLocaleString();
        requestAnimationFrame(updateCounter);
      } else {
        counterElement.textContent = target.toLocaleString() + '+';
      }
    }

    // Start animation when visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          updateCounter();
          observer.unobserve(entry.target);
        }
      });
    });

    observer.observe(counterElement);
  }

  // ==========================================================================
  // Testimonials Slider
  // ==========================================================================
  function initTestimonialsSlider() {
    const testimonials = document.querySelectorAll('.tc-testimonial');
    const dots = document.querySelectorAll('.tc-dot');
    if (testimonials.length === 0 || dots.length === 0) return;

    let currentIndex = 0;
    let autoRotateInterval;

    function showTestimonial(index) {
      testimonials.forEach(t => t.classList.remove('active'));
      dots.forEach(d => d.classList.remove('active'));

      testimonials[index].classList.add('active');
      dots[index].classList.add('active');
      currentIndex = index;
    }

    function nextTestimonial() {
      const nextIndex = (currentIndex + 1) % testimonials.length;
      showTestimonial(nextIndex);
    }

    function startAutoRotate() {
      autoRotateInterval = setInterval(nextTestimonial, 5000);
    }

    function stopAutoRotate() {
      if (autoRotateInterval) {
        clearInterval(autoRotateInterval);
      }
    }

    // Add click handlers to dots
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        stopAutoRotate();
        showTestimonial(index);
        startAutoRotate();
      });
    });

    // Start auto-rotation
    startAutoRotate();
  }

  // ==========================================================================
  // Poll Widget - "Favorite Universe"
  // ==========================================================================
  function initPollWidget() {
    const pollWidget = document.querySelector('.tc-poll-widget');
    if (!pollWidget) return;

    const options = pollWidget.querySelectorAll('.tc-poll-option');
    const resultsContainer = pollWidget.querySelector('.tc-poll-results');
    const pollBars = resultsContainer?.querySelectorAll('.tc-poll-bar');

    if (!options.length || !resultsContainer) return;

    // Check if user already voted
    const hasVoted = localStorage.getItem('tc-poll-voted');

    if (hasVoted) {
      showResults();
      return;
    }

    // Add click handlers to options
    options.forEach(option => {
      option.addEventListener('click', () => {
        // Remove selected class from all options
        options.forEach(o => o.classList.remove('selected'));
        
        // Add selected class to clicked option
        option.classList.add('selected');

        // Simulate vote submission (replace with actual API call)
        setTimeout(() => {
          localStorage.setItem('tc-poll-voted', 'true');
          showResults();
          trackEngagement('poll_vote', option.textContent.trim());
        }, 500);
      });
    });

    function showResults() {
      // Hide options, show results
      options.forEach(o => o.style.display = 'none');
      resultsContainer.classList.add('active');

      // Animate bars
      setTimeout(() => {
        pollBars?.forEach(bar => {
          const width = bar.getAttribute('data-width');
          bar.style.width = width + '%';
        });
      }, 100);
    }
  }

  // ==========================================================================
  // Quiz Widget - "What's Your Superhero Identity?"
  // ==========================================================================
  function initQuizWidget() {
    const quizWidget = document.querySelector('.tc-quiz-widget');
    if (!quizWidget) return;

    const questions = [
      {
        question: "What's your greatest strength?",
        options: [
          { text: "Intelligence & Strategy", archetype: "genius" },
          { text: "Physical Power", archetype: "powerhouse" },
          { text: "Speed & Agility", archetype: "speedster" },
          { text: "Leadership & Courage", archetype: "leader" }
        ]
      },
      {
        question: "How do you approach problems?",
        options: [
          { text: "Analyze from all angles", archetype: "genius" },
          { text: "Charge in headfirst", archetype: "powerhouse" },
          { text: "Find the quickest solution", archetype: "speedster" },
          { text: "Rally others to help", archetype: "leader" }
        ]
      },
      {
        question: "What's your ideal superpower?",
        options: [
          { text: "Mind control", archetype: "genius" },
          { text: "Super strength", archetype: "powerhouse" },
          { text: "Super speed", archetype: "speedster" },
          { text: "Flight", archetype: "leader" }
        ]
      },
      {
        question: "What motivates you to be a hero?",
        options: [
          { text: "To protect knowledge", archetype: "genius" },
          { text: "To defend the weak", archetype: "powerhouse" },
          { text: "To make a quick impact", archetype: "speedster" },
          { text: "To inspire others", archetype: "leader" }
        ]
      },
      {
        question: "Choose your weapon:",
        options: [
          { text: "High-tech gadgets", archetype: "genius" },
          { text: "Fists of fury", archetype: "powerhouse" },
          { text: "Lightning-fast strikes", archetype: "speedster" },
          { text: "Inspiring banner", archetype: "leader" }
        ]
      }
    ];

    const quizResults = {
      genius: {
        title: "🧠 The Mastermind",
        description: "You're the strategic genius behind every victory! Like Batman or Iron Man, you use your intellect to outsmart any foe.",
        ctaText: "Get Your Genius Portrait"
      },
      powerhouse: {
        title: "💪 The Powerhouse",
        description: "Raw strength and determination define you! Like Superman or Thor, you're the shield that protects everyone.",
        ctaText: "Get Your Powerhouse Portrait"
      },
      speedster: {
        title: "⚡ The Speedster",
        description: "You move faster than anyone can comprehend! Like The Flash or Quicksilver, you're always one step ahead.",
        ctaText: "Get Your Speedster Portrait"
      },
      leader: {
        title: "👑 The Leader",
        description: "You inspire others to greatness! Like Captain America or Wonder Woman, you lead by example.",
        ctaText: "Get Your Leader Portrait"
      }
    };

    let currentQuestion = 0;
    const archetypeCounts = { genius: 0, powerhouse: 0, speedster: 0, leader: 0 };

    function renderQuestion() {
      const questionContainer = quizWidget.querySelector('.tc-quiz-question-container');
      if (!questionContainer) return;

      const question = questions[currentQuestion];
      
      questionContainer.innerHTML = `
        <div class="tc-quiz-question">${question.question}</div>
        <div class="tc-quiz-options">
          ${question.options.map((option, index) => `
            <div class="tc-quiz-option" data-archetype="${option.archetype}">
              ${option.text}
            </div>
          `).join('')}
        </div>
      `;

      // Add click handlers
      const options = questionContainer.querySelectorAll('.tc-quiz-option');
      options.forEach(option => {
        option.addEventListener('click', () => {
          const archetype = option.getAttribute('data-archetype');
          archetypeCounts[archetype]++;
          
          currentQuestion++;
          
          if (currentQuestion < questions.length) {
            renderQuestion();
          } else {
            showResult();
          }
        });
      });
    }

    function showResult() {
      const questionContainer = quizWidget.querySelector('.tc-quiz-question-container');
      if (!questionContainer) return;

      // Find dominant archetype
      const dominantArchetype = Object.keys(archetypeCounts).reduce((a, b) => 
        archetypeCounts[a] > archetypeCounts[b] ? a : b
      );

      const result = quizResults[dominantArchetype];

      questionContainer.innerHTML = `
        <div class="tc-quiz-result active">
          <div class="tc-quiz-result-title">${result.title}</div>
          <div class="tc-quiz-result-desc">${result.description}</div>
          <a href="/products/custom-superhero-portrait" class="tc-quiz-cta">${result.ctaText}</a>
        </div>
      `;

      trackEngagement('quiz_complete', result.title);
    }

    // Initialize quiz
    renderQuestion();
  }

  // ==========================================================================
  // Newsletter Signup
  // ==========================================================================
  function initNewsletter() {
    const newsletterForm = document.querySelector('.tc-newsletter-form');
    if (!newsletterForm) return;

    newsletterForm.addEventListener('submit', (e) => {
      e.preventDefault();
      
      const emailInput = newsletterForm.querySelector('.tc-newsletter-input');
      const email = emailInput?.value;
      
      if (!email) return;

      // Simulate form submission (replace with actual API call)
      const submitButton = newsletterForm.querySelector('.tc-newsletter-submit');
      const originalText = submitButton?.textContent;
      
      if (submitButton) {
        submitButton.textContent = 'Joining...';
        submitButton.disabled = true;
      }

      setTimeout(() => {
        // Store email (replace with actual API call)
        localStorage.setItem('tc-newsletter-email', email);
        
        if (submitButton) {
          submitButton.textContent = 'Joined! ✓';
          submitButton.style.background = '#10b981';
        }

        trackEngagement('newsletter_signup', 'email_submitted');

        // Reset after 3 seconds
        setTimeout(() => {
          if (submitButton) {
            submitButton.textContent = originalText;
            submitButton.disabled = false;
            submitButton.style.background = '';
          }
          if (emailInput) {
            emailInput.value = '';
          }
        }, 3000);
      }, 1000);
    });
  }

  // ==========================================================================
  // Lazy Loading Images
  // ==========================================================================
  function initLazyLoading() {
    if ('loading' in HTMLImageElement.prototype) {
      const images = document.querySelectorAll('img[loading="lazy"]');
      images.forEach(img => {
        img.loading = 'lazy';
      });
    } else {
      // Fallback for browsers that don't support native lazy loading
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lazysizes/5.3.2/lazysizes.min.js';
      document.body.appendChild(script);
    }
  }

  // ==========================================================================
  // Engagement Tracking
  // ==========================================================================
  function trackEngagement(action, label) {
    console.log('Track:', action, label);
    
    // Google Analytics 4 integration (if available)
    if (typeof gtag !== 'undefined') {
      gtag('event', action, {
        event_category: 'Blog Engagement',
        event_label: label
      });
    }

    // Shopify analytics integration (if available)
    if (typeof window.ShopifyAnalytics !== 'undefined') {
      window.ShopifyAnalytics.meta = window.ShopifyAnalytics.meta || {};
      window.ShopifyAnalytics.meta.page = window.ShopifyAnalytics.meta.page || {};
      window.ShopifyAnalytics.meta.page.action = action;
    }
  }

  // ==========================================================================
  // Smooth Scroll
  // ==========================================================================
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;
        
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // ==========================================================================
  // Animation on Scroll
  // ==========================================================================
  function initScrollAnimations() {
    const animatedElements = document.querySelectorAll('.tc-blog-card, .tc-featured-post, .tc-stat');
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;
          target.style.opacity = '0';
          target.style.transform = 'translateY(20px)';
          
          setTimeout(() => {
            target.style.transition = 'all 0.5s ease';
            target.style.opacity = '1';
            target.style.transform = 'translateY(0)';
          }, 100);
          
          observer.unobserve(target);
        }
      });
    }, {
      threshold: 0.1
    });

    animatedElements.forEach(el => observer.observe(el));
  }

  // ==========================================================================
  // Track Link Clicks
  // ==========================================================================
  function initLinkTracking() {
    // Track blog post clicks
    document.querySelectorAll('.tc-read-more-btn, .tc-card-link, .tc-blog-card').forEach(link => {
      link.addEventListener('click', function() {
        const card = this.closest('.tc-blog-card, .tc-featured-post');
        const title = card?.querySelector('.tc-post-title, .tc-card-title')?.textContent || 'Unknown';
        trackEngagement('click', 'Read Blog Post: ' + title);
      });
    });

    // Track CTA clicks
    document.querySelectorAll('.tc-cta-button, .tc-quiz-cta').forEach(button => {
      button.addEventListener('click', function() {
        trackEngagement('click', 'CTA Button: ' + this.textContent.trim());
      });
    });

    // Track category clicks
    document.querySelectorAll('.tc-category-tag').forEach(tag => {
      tag.addEventListener('click', function() {
        trackEngagement('click', 'Category Filter: ' + this.textContent.trim());
      });
    });
  }

  // ==========================================================================
  // Mobile Menu Toggle (if needed)
  // ==========================================================================
  function initMobileMenu() {
    const menuToggle = document.querySelector('.tc-menu-toggle');
    const mobileMenu = document.querySelector('.tc-mobile-menu');
    
    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        mobileMenu.classList.toggle('active');
      });
    }
  }

  // ==========================================================================
  // Initialize All Widgets
  // ==========================================================================
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initWidgets);
    } else {
      initWidgets();
    }

    function initWidgets() {
      initFanCounter();
      initTestimonialsSlider();
      initPollWidget();
      initQuizWidget();
      initNewsletter();
      initLazyLoading();
      initSmoothScroll();
      initScrollAnimations();
      initLinkTracking();
      initMobileMenu();
      
      console.log('TurnedComics Blog widgets initialized');
    }
  }

  // Start initialization
  init();

})();
